import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore, useAppStore, api } from "@/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Upload, FileSpreadsheet, Send, Loader2, Eye, Trash2, Archive,
  AlertTriangle, RotateCcw, PackageOpen, Pencil, Download, Copy
} from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";

export default function TemplateManagerPage() {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const navigate = useNavigate();
  const { templates, periods, units, fetchTemplates, fetchPeriods, fetchUnits } = useAppStore();
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishTemplateId, setPublishTemplateId] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [publishDeadline, setPublishDeadline] = useState("");
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [duplicateDialog, setDuplicateDialog] = useState(null); // { id, name }
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    if (activeTab === "active") fetchTemplates(false);
    else fetchTemplates(false, activeTab === "archived" ? "archived" : "trashed");
    fetchPeriods();
    fetchUnits();
  }, [activeTab]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx")) { toast.error("Само .xlsx файлове са поддържани"); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/templates/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Шаблон качен: ${res.data.sheet_count} листа, ${res.data.editable_count} ${t("editableCount")}`);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handlePublish = async () => {
    if (!publishTemplateId || !selectedPeriod) return;
    setPublishing(true);
    try {
      const payload = { period_id: selectedPeriod, target_units: "all" };
      if (publishDeadline) payload.deadline = publishDeadline;
      const res = await api.post(`/templates/${publishTemplateId}/publish`, payload);
      toast.success(`Публикувано към ${res.data.created} звена`);
      setShowPublishDialog(false);
      setPublishDeadline("");
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
    finally { setPublishing(false); }
  };

  const handleArchive = async (id) => {
    try { await api.post(`/templates/${id}/archive`); toast.success(t("templateArchived")); refreshTab(); }
    catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleRestore = async (id) => {
    try { await api.post(`/templates/${id}/restore`); toast.success(t("templateRestored")); refreshTab(); }
    catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleTrash = async (id) => {
    try { await api.post(`/templates/${id}/trash`); toast.success(t("templateTrashed")); refreshTab(); }
    catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleUntrash = async (id) => {
    try { await api.post(`/templates/${id}/untrash`); toast.success(t("templateUntrashedToArchive")); refreshTab(); }
    catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handlePermanentDelete = async () => {
    if (!deleteTarget || permanentDeleteConfirm !== "DELETE") return;
    try {
      await api.delete(`/templates/${deleteTarget.id}/permanent`, { data: { confirm: "DELETE" } });
      toast.success(t("templatePermanentlyDeleted"));
      setShowPermanentDeleteDialog(false);
      setPermanentDeleteConfirm("");
      setDeleteTarget(null);
      refreshTab();
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };

  const handleDuplicate = async () => {
    if (!duplicateDialog || !duplicateName.trim()) return;
    setDuplicating(true);
    try {
      const res = await api.post(`/templates/${duplicateDialog.id}/duplicate`, { name: duplicateName.trim() });
      toast.success(`${t("templateDuplicated")}: ${res.data.name}`);
      setDuplicateDialog(null);
      setDuplicateName("");
      refreshTab();
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
    finally { setDuplicating(false); }
  };

  const refreshTab = () => {
    if (activeTab === "active") fetchTemplates(false);
    else fetchTemplates(false, activeTab === "archived" ? "archived" : "trashed");
  };

  const loadTemplateDetails = async (id) => {
    try { const res = await api.get(`/templates/${id}`); setSelectedTemplate(res.data); }
    catch { toast.error(t("loadFailed")); }
  };

  if (!isAdmin()) return (
    <div className="flex items-center justify-center h-full"><p className="text-muted-foreground">{t("adminOnly")}</p></div>
  );

  const renderTemplateCard = (tp) => {
    const isActive = (tp.deletion_status || "active") === "active";
    const isArchived = tp.deletion_status === "archived";
    const isTrashed = tp.deletion_status === "trashed";

    return (
      <Card key={tp.id} data-testid={`template-card-${tp.id}`}
        className={`border shadow-sm hover:shadow-md transition-shadow ${
          isTrashed ? "border-red-200 bg-red-50/30 opacity-75" :
          isArchived ? "border-amber-200 bg-amber-50/30 opacity-85" :
          "border-slate-200"
        }`}>
        <CardHeader className="py-4 px-5 border-b border-slate-100">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-[#14532d]" />{tp.name}
            {isArchived && <span className="text-[10px] font-normal text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{t("archived")}</span>}
            {isTrashed && <span className="text-[10px] font-normal text-red-500 bg-red-50 px-1.5 py-0.5 rounded">{t("trashTab")}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-slate-50 rounded-md p-2.5"><p className="text-slate-400 uppercase tracking-wider mb-0.5">{t("sheets")}</p><p className="font-semibold text-slate-700">{tp.sheet_count}</p></div>
            <div className="bg-slate-50 rounded-md p-2.5"><p className="text-slate-400 uppercase tracking-wider mb-0.5">{t("cells")}</p><p className="font-semibold text-slate-700">{tp.cell_count?.toLocaleString()}</p></div>
            <div className="bg-amber-50 rounded-md p-2.5"><p className="text-amber-500 uppercase tracking-wider mb-0.5">{t("editableCount")}</p><p className="font-semibold text-amber-700">{tp.editable_count}</p></div>
            <div className="bg-blue-50 rounded-md p-2.5"><p className="text-blue-500 uppercase tracking-wider mb-0.5">{t("formulasCount")}</p><p className="font-semibold text-blue-700">{tp.formula_count}</p></div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid={`view-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs" onClick={() => loadTemplateDetails(tp.id)}>
              <Eye className="w-3.5 h-3.5 mr-1" />{t("preview")}
            </Button>
            <Button data-testid={`download-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={async () => {
                try {
                  const res = await api.get(`/templates/${tp.id}/download`, { responseType: "blob" });
                  const a = document.createElement("a"); a.href = window.URL.createObjectURL(new Blob([res.data]));
                  a.download = `${tp.name}.xlsx`; a.click();
                  toast.success(t("downloaded"));
                } catch { toast.error(t("exportFailed")); }
              }}>
              <Download className="w-3.5 h-3.5 mr-1" />{t("downloadTemplate")}
            </Button>
            <Button data-testid={`duplicate-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs text-violet-600 border-violet-200 hover:bg-violet-50"
              onClick={() => { setDuplicateDialog({ id: tp.id, name: tp.name }); setDuplicateName(`${tp.name} (копие)`); }}>
              <Copy className="w-3.5 h-3.5 mr-1" />{t("duplicateTemplate")}
            </Button>
            {isActive && (
              <>
                <Button data-testid={`edit-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs text-[#14532d] border-emerald-200 hover:bg-emerald-50"
                  onClick={() => navigate(`/template-editor/${tp.id}`)}>
                  <Pencil className="w-3.5 h-3.5 mr-1" />{t("editTemplate")}
                </Button>
                <Button data-testid={`publish-template-${tp.id}`} size="sm" className="h-8 text-xs bg-[#14532d] hover:bg-[#166534] text-white"
                  onClick={() => { setPublishTemplateId(tp.id); setShowPublishDialog(true); }}>
                  <Send className="w-3.5 h-3.5 mr-1" />{t("publish")}
                </Button>
                <Button data-testid={`archive-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => handleArchive(tp.id)}>
                  <Archive className="w-3.5 h-3.5 mr-1" />{t("archive")}
                </Button>
                <Button data-testid={`delete-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs text-red-500 border-red-200 hover:bg-red-50"
                  onClick={() => { setDeleteTarget(tp); setShowPermanentDeleteDialog(true); setPermanentDeleteConfirm(""); }}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />{t("delete")}
                </Button>
              </>
            )}
            {isArchived && (
              <>
                <Button data-testid={`restore-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs text-green-600 border-green-200 hover:bg-green-50"
                  onClick={() => handleRestore(tp.id)}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />{t("restore")}
                </Button>
                <Button data-testid={`trash-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs text-red-500 border-red-200 hover:bg-red-50"
                  onClick={() => handleTrash(tp.id)}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />{t("moveToTrash")}
                </Button>
              </>
            )}
            {isTrashed && (
              <>
                <Button data-testid={`untrash-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => handleUntrash(tp.id)}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />{t("restoreFromTrash")}
                </Button>
                <Button data-testid={`permanent-delete-template-${tp.id}`} variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => { setDeleteTarget(tp); setShowPermanentDeleteDialog(true); setPermanentDeleteConfirm(""); }}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />{t("permanentDelete")}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const emptyState = (message) => (
    <Card className="col-span-full border-2 border-dashed border-slate-200">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <PackageOpen className="w-12 h-12 text-slate-300 mb-4" />
        <p className="text-sm font-medium text-slate-500">{message}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 lg:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="templates-title" className="text-3xl font-semibold tracking-tight text-[#14532d]">{t("templateManager")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("templateManagerDesc")}</p>
        </div>
        <div className="flex items-center gap-3">
          <label htmlFor="upload-template">
            <Button data-testid="upload-template-button" asChild disabled={uploading} className="h-10 bg-[#14532d] hover:bg-[#166534] cursor-pointer text-white">
              <span>{uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}{t("uploadTemplate")}</span>
            </Button>
          </label>
          <input id="upload-template" data-testid="upload-template-input" type="file" accept=".xlsx" onChange={handleUpload} className="hidden" />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 h-10">
          <TabsTrigger data-testid="tab-active-templates" value="active" className="text-sm">
            <FileSpreadsheet className="w-4 h-4 mr-2" />{t("activeTab")}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-archived-templates" value="archived" className="text-sm">
            <Archive className="w-4 h-4 mr-2" />{t("archivedTab")}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-trashed-templates" value="trashed" className="text-sm">
            <Trash2 className="w-4 h-4 mr-2" />{t("trashTab")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.length === 0 ? emptyState(t("noTemplates")) : templates.map(renderTemplateCard)}
          </div>
        </TabsContent>
        <TabsContent value="archived" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.length === 0 ? emptyState(t("noArchivedTemplates")) : templates.map(renderTemplateCard)}
          </div>
        </TabsContent>
        <TabsContent value="trashed" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.length === 0 ? emptyState(t("noTrashedTemplates")) : templates.map(renderTemplateCard)}
          </div>
        </TabsContent>
      </Tabs>

      {/* Publish Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("publishToUnits")}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>{t("selectPeriod")}</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger data-testid="publish-period-select" className="mt-1"><SelectValue placeholder={t("choosePeriod")} /></SelectTrigger>
                <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name || p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("deadline")}</Label>
              <Input data-testid="publish-deadline-input" type="date" value={publishDeadline}
                onChange={(e) => setPublishDeadline(e.target.value)} className="mt-1" />
            </div>
            <p className="text-sm text-muted-foreground">{t("publishInfo").replace("{count}", units.length)}</p>
            <Button data-testid="confirm-publish-button" onClick={handlePublish} disabled={!selectedPeriod || publishing} className="w-full bg-[#14532d] hover:bg-[#166534] text-white">
              {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}{t("publishToAll")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Dialog */}
      <Dialog open={showPermanentDeleteDialog} onOpenChange={(open) => { setShowPermanentDeleteDialog(open); if (!open) setPermanentDeleteConfirm(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />{t("permanentDeleteConfirm")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-700">{t("permanentDeleteWarning")}</p>
            {deleteTarget && <p className="text-sm font-medium">{deleteTarget.name}</p>}
            <div>
              <Label className="text-xs text-red-600">{t("typeDeleteConfirm")}</Label>
              <Input data-testid="permanent-delete-confirm-input" value={permanentDeleteConfirm}
                onChange={(e) => setPermanentDeleteConfirm(e.target.value)}
                placeholder="DELETE" className="mt-1 border-red-200 focus:ring-red-500" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowPermanentDeleteDialog(false)}>{t("close")}</Button>
              <Button data-testid="confirm-permanent-delete" disabled={permanentDeleteConfirm !== "DELETE"}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handlePermanentDelete}>
                <Trash2 className="w-4 h-4 mr-2" />{t("permanentDelete")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {selectedTemplate && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="py-4 px-6 border-b border-slate-100 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-800">{t("templatePreview")}: {selectedTemplate.name}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setSelectedTemplate(null)}>{t("close")}</Button>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              {selectedTemplate.sheets?.map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2 px-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-slate-400 w-6">{s.order_index + 1}</span>
                    <span className="text-sm font-medium text-slate-700">{s.sheet_name}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>{t("rows")}: {s.max_row}</span>
                    <span>{t("cols")}: {s.max_col}</span>
                    <span>{t("merged")}: {s.merged_cells?.length || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Duplicate Template Dialog */}
      <Dialog open={!!duplicateDialog} onOpenChange={open => { if (!open) setDuplicateDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-4 h-4 text-violet-600" /> {t("duplicateTemplate")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-slate-500">{t("duplicateDesc")}: <strong>{duplicateDialog?.name}</strong></p>
            <div>
              <Label className="text-xs font-medium text-slate-700 mb-1">{t("newName")}</Label>
              <Input data-testid="duplicate-name" value={duplicateName} onChange={e => setDuplicateName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleDuplicate(); }}
                className="h-9 text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setDuplicateDialog(null)}>{t("close")}</Button>
            <Button data-testid="duplicate-confirm" size="sm" onClick={handleDuplicate}
              disabled={duplicating || !duplicateName.trim()}
              className="bg-violet-600 hover:bg-violet-700 text-white">
              {duplicating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />}
              {t("duplicate")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
